import { Dispatch, useEffect, useState } from 'react';
import { PaymentMethod, StripeError } from '@stripe/stripe-js';
import { core } from '@tock/coretypes';
import { showError } from '@tock/toast';
import { useDispatch } from 'react-redux';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { useSetupIntent } from './useSetupIntent';

const defaultOnError = (dispatch: Dispatch<unknown>) => {
  return (err: unknown) => {
    try {
      const stripeError = err as StripeError;
      if (stripeError.type === 'card_error') {
        // It is a conscious decision to not log to rollbar here, we do not need to log issues that are not within our
        // control. Especially with rollbar costing us per event
        dispatch(showError(`${stripeError.message ?? 'Failed to save card'}`));
        return;
      } else if ('LOGGER' in window) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window.LOGGER as any).info('Failed to save card', `${stripeError.message ?? 'no error'}`);
        return;
      }
      // eslint-disable-next-line no-empty
    } catch (err) {}
    dispatch(showError('Failed to save card'));
  };
};

type AsyncPaymentCardSupplier = (fields: { name: string }) => Promise<core.IPaymentCard>;

export interface NewCardPaymentElementData {
  isInitialized: boolean;
  isLocked: boolean;
  isTokenizing: boolean;
  reset(): void;
  tokenizePaymentCard: AsyncPaymentCardSupplier;
}

const uninitializedNewCardPaymentElementData: NewCardPaymentElementData = {
  isInitialized: false,
  isLocked: false,
  isTokenizing: false,
  reset: () => {
    return;
  },
  tokenizePaymentCard: (fields) => Promise.reject(),
};

export function useNewCardPaymentElement(): NewCardPaymentElementData {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();

  // When we successfully tokenize a card, we will save it until we establish a new client secret
  // This prevents attempting to confirm an already confirmed setup intent, which fails
  const { clientSecret, refreshSetupIntent } = useSetupIntent();

  // Setting this isInitialized in an effect that checks `stripe` is required to make this hook re-run after a `<NewCardPaymentElement>` successfully mounts
  const [isInitialized, setIsInitalized] = useState(false);
  const [isTokenizing, setIsTokenizing] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    if (!stripe) {
      return;
    }
    setIsInitalized(true);
  }, [stripe]);

  if (!stripe || !elements) {
    return uninitializedNewCardPaymentElementData;
  }

  const unlock = () => {
    elements.getElement('payment')?.update({ readOnly: false });
    setIsLocked(false);
  };

  const reset = () => {
    unlock();
    refreshSetupIntent();
  };

  const tokenizePaymentCard: AsyncPaymentCardSupplier = async ({ name }) => {
    setIsTokenizing(true);
    setIsLocked(true);
    elements.getElement('payment')?.update({ readOnly: true });
    const stripeAddressElementEnabled: boolean = elements.getElement('address') != null;

    if (!clientSecret || (!stripeAddressElementEnabled && !name)) {
      unlock();
      // TODO: do we need a message here?
      return Promise.reject();
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      unlock();
      return Promise.reject();
    }

    return stripe
      .confirmSetup({
        clientSecret,
        elements,
        confirmParams: {
          return_url: `${window.location.origin}`,
          expand: ['payment_method'],
          payment_method_data: {
            // This field will be overwritten by values collected in elements, can be removed completely after sanctions billing address collection fully launched
            billing_details: {
              name: name,
            },
          },
        },
        redirect: 'if_required',
      })
      .then(({ error, setupIntent }) => {
        if (error) {
          unlock();
          return Promise.reject(error);
        }

        if (!setupIntent) {
          unlock();
          return Promise.reject(new Error('SetupIntent returned undefined/null from Stripe'));
        }

        // Stripe is sometimes not honoring the expand: ['payment_method'] parameter and returning just a PaymentMethod ID here.
        // This branching is a workaround until Stripe fixes it.
        // Alternatively since we are re-retrieving the PaymentMethod in ApiServer, we could only send back the PaymentMethod ID in the future for all payment card processing
        if (typeof setupIntent.payment_method === 'string') {
          const card = {
            name: '',
            nonceToken: setupIntent.payment_method,
            maskedNumber: '',
            processorType: 'STRIPE',
            country: '',
            zipCode: '',
            expandedPaymentCard: false,
          } as core.IPaymentCard;
          return card;
        }
        const paymentMethod = setupIntent.payment_method as PaymentMethod;
        const card = {
          name: paymentMethod.billing_details?.name,
          nonceToken: paymentMethod.id,
          maskedNumber: paymentMethod.card?.last4,
          brand: paymentMethod.card?.brand,
          processorType: 'STRIPE',
          country: paymentMethod.billing_details.address?.country ?? undefined,
          zipCode: paymentMethod.billing_details.address?.postal_code ?? undefined,
          expandedPaymentCard: true,
        } as core.IPaymentCard;
        return card;
      })
      .catch((err) => {
        useNewCardPaymentElement;
        // @ts-ignore-error typing issue with redux 8
        defaultOnError(dispatch)(err);
        throw err;
      })
      .finally(() => {
        setIsTokenizing(false);
      });
  };

  return {
    isInitialized,
    isLocked,
    isTokenizing,
    reset,
    tokenizePaymentCard,
  };
}
